import React from 'react';
import { useSearchParams } from 'react-router-dom';
import PostludePanel from './PostludePanel';
import './PostludeEmbedPage.css';

const THEME_VARS: Record<'light' | 'dark', Record<string, string>> = {
  dark: {
    '--color-canvas': '#0f1419',
    '--color-surface': '#1a222c',
    '--color-surface-muted': '#243040',
    '--color-text': '#e8eef5',
    '--color-text-muted': '#9aadc2',
    '--color-border': '#334155',
    '--color-accent': '#3b82f6',
    '--color-success': '#3dd68c',
  },
  light: {
    '--color-canvas': '#f4f1ea',
    '--color-surface': '#ffffff',
    '--color-surface-muted': '#ebe6dc',
    '--color-text': '#1c1917',
    '--color-text-muted': '#57534e',
    '--color-border': '#d6d3d1',
    '--color-accent': '#1e3a5f',
    '--color-success': '#15803d',
  },
};

function applyTheme(root: HTMLElement, theme: 'light' | 'dark', overrides?: Record<string, string>) {
  const vars = { ...THEME_VARS[theme], ...(overrides || {}) };
  Object.entries(vars).forEach(([key, value]) => {
    if (/^--[a-z0-9-]+$/i.test(key) && typeof value === 'string' && !/[<>'"`]|url\(|expression\(/i.test(value)) {
      root.style.setProperty(key, value);
    }
  });
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}

const PostludeEmbedPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();
  const themeParam = (params.get('theme') || 'dark').toLowerCase() === 'light' ? 'light' : 'dark';
  const shell = React.useRef<HTMLDivElement | null>(null);
  const [error, setError] = React.useState('');

  const gatewayBaseUrl = React.useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.origin.replace(/\/$/, '');
  }, []);

  React.useEffect(() => {
    if (!token) {
      setError('This Postlude window needs an embed token from the host site.');
    }
  }, [token]);

  React.useEffect(() => {
    const root = document.documentElement;
    applyTheme(root, themeParam);
    function onMessage(event: MessageEvent) {
      if (event.source !== window.parent) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'postlude:theme') return;
      const nextTheme = data.theme === 'light' ? 'light' : 'dark';
      const cssVariables = data.cssVariables && typeof data.cssVariables === 'object'
        ? (data.cssVariables as Record<string, string>)
        : undefined;
      applyTheme(root, nextTheme, cssVariables);
    }
    window.addEventListener('message', onMessage);
    window.parent.postMessage({ type: 'postlude:ready', theme: themeParam }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, [themeParam]);

  React.useEffect(() => {
    const node = shell.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const report = () => {
      const height = Math.ceil(node.getBoundingClientRect().height);
      window.parent.postMessage({ type: 'postlude:height', height }, '*');
    };
    report();
    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="postlude-embed" ref={shell} data-theme={themeParam}>
      {error ? (
        <p className="postlude-embed__error" role="alert">{error}</p>
      ) : (
        <PostludePanel
          language="en"
          gatewayBaseUrl={gatewayBaseUrl}
          accessToken={token}
        />
      )}
    </div>
  );
};

export default PostludeEmbedPage;
